import { SuggestionData } from 'ckeditor5-premium-features'
import { produce } from 'immer'

import { TrackedChange } from 'models/changes'
import { useShallow } from 'zustand/react/shallow'
import { createWithEqualityFn } from 'zustand/traditional'

export const defaultSuggestionCardFilter = 'Filter by resolution type'

// Modify in SuggestionStore.ts
const deepEqualityFnLimitedWithKey = (a: unknown, b: unknown): boolean => {
    // Use a simpler shallow equality check for most objects
    if (typeof a === 'object' && typeof b === 'object') {
        // Only do deep checks on small objects
        if (a === null || b === null) return a === b

        // For arrays of suggestions, check only length and IDs
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a?.length !== b?.length) return false
            // For suggestion arrays, check just IDs not content
            if (a?.length > 0 && 'id' in a?.[0]) {
                return a?.every((item, i) => item?.id === b?.[i].id)
            }
        }
    }
    return Object.is(a, b) // Default to simple equality
}

export interface SuggestionDataMap {
    id: string
    suggestions: SuggestionData[]
}
interface SuggestionStore {
    // editorAnnotationsRef: React.MutableRefObject<null>
    //make key as string and map it to Editor
    suggestionData: Record<string, SuggestionDataMap>
    // Add methods to update suggestion data and remove suggestions.
    trackChangesData: Record<string, TrackedChange>
    //suggestion card filter
    suggestionCardFilter: string
    //should show history suggestions
    isSuggestionsDataUpdated?: boolean
    // set card should loading state
    isLoadingCards?: boolean

    /**set and remove track change data */
    setTrackChangesData: (
        trackChange: Record<string, TrackedChange>,
        removeIds?: string[]
    ) => void
    /**set and remove suggestion */
    addOrUpdateSuggestion: (suggestion: SuggestionDataMap) => void
    removeSuggestionData: (id: string) => void
    /**set and remove bulk suggestions*/
    addOrUpdateSuggestions: (
        dataMap: Record<string, SuggestionDataMap>,
        updateMode: 'merge' | 'replace'
    ) => void
    /**remove bulk suggestions*/
    removeBulkSuggestionData: (ids: string[]) => void
    /**remove all suggestions*/
    removeAllSuggestionData: () => void
    /**get Suggestion with id*/
    getSuggestionData: (id: string) => SuggestionDataMap | undefined
    /**get all suggestion data*/
    getAllSuggestionData: () => Record<string, SuggestionDataMap>
    /**set suggestion card filter */
    setSuggestionCardFilter: (filter: string) => void
    /**set should show history suggestions */
    setIsSuggestionsDataUpdated: (shouldShow: boolean) => void
    /**set state to show cards are loading or not */
    setIsLoadingCards: (isLoadingCards: boolean) => void
    /** Reset suggestion scroll state */
    resetSuggestionScroll: boolean
    /** Set reset suggestion scroll state */
    setResetSuggestionScroll: (value: boolean) => void
}
/**
 * Creates a zustand store for managing workspace elementItemValue state
 * @returns {ElementItemStoreType} A reactive store with workspace management methods
 */
export const useSuggestionsStore = createWithEqualityFn<SuggestionStore>()(
    (set, get) => {
        // internal helper to remove suggestions by ID or clear all
        const removeSuggestionsHelper = (ids?: string[]) =>
            set((state) => {
                const updated = { ...state.suggestionData }
                if (ids) {
                    ids.forEach((id) => delete updated[id])
                } else {
                    Object.keys(updated).forEach((id) => delete updated[id])
                }
                return { suggestionData: updated }
            })

        return {
            suggestionData: {},
            trackChangesData: {},
            suggestionCardFilter: defaultSuggestionCardFilter,
            isSuggestionsDataUpdated: false,
            isLoadingCards: false,

            /** Replace or update trackChangesData */
            setTrackChangesData: (
                trackChange: Record<string, TrackedChange>,
                removeIds?: string[]
            ) => {
                set(
                    produce((draft) => {
                        if (
                            !trackChange ||
                            Object.keys(trackChange).length === 0
                        ) {
                            draft.trackChangesData = {}
                            return
                        }

                        if (removeIds?.length) {
                            removeIds.forEach((id) => {
                                delete draft.trackChangesData[id]
                            })
                        }

                        for (const [id, change] of Object.entries(
                            trackChange
                        )) {
                            draft.trackChangesData[id] = change
                        }
                    })
                )
            },

            /** Add or update a single suggestion */
            addOrUpdateSuggestion: (suggestion: SuggestionDataMap) =>
                set(
                    produce((draft) => {
                        draft.suggestionData[suggestion.id] = suggestion
                    })
                ),

            /** Remove a single suggestion by ID */
            removeSuggestionData: (id: string) => removeSuggestionsHelper([id]),

            /** Add or update suggestions, optionally replacing existing ones */
            addOrUpdateSuggestions: (
                dataMap: Record<string, SuggestionDataMap>,
                updateMode: 'merge' | 'replace' = 'merge'
            ) =>
                set(
                    produce<SuggestionStore>((draft) => {
                        if (updateMode === 'replace') {
                            const preserveSet = new Set(Object.keys(dataMap))
                            const existingKeys = Object.keys(
                                draft.suggestionData
                            )

                            existingKeys.forEach((key) => {
                                if (!preserveSet.has(key)) {
                                    delete draft.suggestionData[key]
                                }
                            })
                        }

                        // Skip update if there are no real changes
                        const hasChanges = Object.keys(dataMap).some(
                            (key) =>
                                !draft.suggestionData[key] ||
                                !deepEqualityFnLimitedWithKey(
                                    draft.suggestionData[key],
                                    dataMap[key]
                                )
                        )

                        if (hasChanges) {
                            Object.assign(draft.suggestionData, dataMap)
                        }

                        // 🔍 Optional: debug log for development
                        if (process.env.NODE_ENV === 'development') {
                            console.debug(
                                'Updated suggestions:',
                                Object.keys(dataMap).length
                            )
                        }
                    })
                ),

            /** Remove multiple suggestions by IDs */
            removeBulkSuggestionData: (ids: string[]) =>
                removeSuggestionsHelper(ids),

            /** Clear all suggestions */
            removeAllSuggestionData: () => removeSuggestionsHelper(),

            /** Selector to get one suggestion */
            getSuggestionData: (id: string) => get().suggestionData[id],

            /** Selector to get all suggestions */
            getAllSuggestionData: () => get().suggestionData,

            /** Set the active filter */
            setSuggestionCardFilter: (filter: string) =>
                set(() => ({ suggestionCardFilter: filter })),

            /** Update the suggestions "dirty" state */
            setIsSuggestionsDataUpdated: (shouldShow: boolean) =>
                set(() => ({ isSuggestionsDataUpdated: shouldShow })),

            /** Toggle loading state */
            setIsLoadingCards: (isLoadingCards: boolean) =>
                set(() => ({ isLoadingCards })),
            /** Reset suggestion scroll state */
            resetSuggestionScroll: false,
            /** Set reset suggestion scroll state */
            setResetSuggestionScroll: (value: boolean) =>
                set({ resetSuggestionScroll: value }),
        }
    }
)

/**
 * A custom hook that retrieves a shallowly selected value from the `useSuggestionsStore` store.
 *
 * Sample usage:
 * ```typescript
 * const { suggestionData } = useSuggestionsShallowSelector((state) => ({
 *   suggestionData: state.suggestionData,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const useSuggestionsShallowSelector = <T>(
    selector: (state: SuggestionStore) => T
): T => useSuggestionsStore(useShallow(selector))

/**
 * Selectors to retrieve specific parts of the suggestion store state.
 */
export const selectSuggestionById = (id: string) => (state: SuggestionStore) =>
    state.suggestionData[id]

/** Select all suggestions from the store */
export const selectAllSuggestions = (state: SuggestionStore) =>
    state.suggestionData
